import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { fetchGetWhoAmI } from '~/backend/connector-next-api/connector-next-components';
import { ConnectorNextError } from '~/backend/connector-next-api/connector-next-fetcher';
import { startDeadline } from '~/backend/requestDeadline';
import AppConfig from '~/utils/appconfig';

/**
 * How long the connector has to say who is calling.
 *
 * A connector that accepts the connection and then stops answering would
 * otherwise leave every protected route on a spinner for as long as the tab
 * lives, with no way forward but a reload.
 */
const ANSWER_WITHIN_MS = 15_000;

/**
 * How long a transient failure (a network blip, a 5xx) is retried. Ends well
 * inside {@link ANSWER_WITHIN_MS}, so a connector that keeps answering with an
 * error is reported by that error rather than as an expiry.
 */
const RETRY_WITHIN_MS = 8_000;

/**
 * How long one attempt may wait for the connector's answer. The lookup is a
 * session read that answers at once, so an attempt still waiting after this
 * is stuck on a dead socket; ending it leaves room for a retry inside
 * {@link RETRY_WITHIN_MS}.
 */
const ATTEMPT_RESPONSE_TIMEOUT_MS = 5_000;

/**
 * Everything a user may do, named as the connector names them.
 *
 * The connector's own names, so a permission added on the server is usable here
 * the moment it is granted rather than after a matching constant is added on
 * both sides.
 */
export const PERMISSION = {
    systemAdministration: 'SystemAdministration',
    patientView: 'PatientView',
    patientModify: 'PatientModify',
    patientImport: 'PatientImport',
    patientExport: 'PatientExport',
    patientAssign: 'PatientAssign',
} as const;

export type ConnectorIdentity = {
    account: string | undefined;
    displayName: string | undefined;
    permissions: string[];
    /** Whether this installation decides who may use it at all. */
    signInEnabled: boolean;
    /** Whether the connector would not say who the caller is. */
    isUnidentified: boolean;
    /**
     * Whether the connector knows exactly who the caller is and they hold
     * nothing at all — an account recorded but not yet put in a group.
     */
    holdsNothing: boolean;
    /** Whether the request never reached the connector — a transport failure, not an HTTP status. */
    isUnreachable: boolean;
    /**
     * The status of an answer that was neither an identity nor a refusal of one — a 404 from a
     * connector without this endpoint, a 500, a 503 while it restarts. Undefined otherwise.
     */
    failedWithStatus: number | undefined;
    isLoading: boolean;
    /**
     * Whether the caller is still waiting for a first answer — the initial ask, or one asked for again
     * after a failure. False while an answer is being refreshed in the background, which nothing should
     * be shown for.
     */
    isAsking: boolean;
    /**
     * Whether the connector would let the caller do this. Where sign-in is off it lets everyone do
     * everything; before an answer arrives, nothing is assumed.
     */
    can: (permission: string) => boolean;
    /** Whether the caller holds any permission that reaches patient data. */
    readonly reachesPatientData: boolean;
    /**
     * Whether the caller may decide who works a patient. Never where sign-in is off: there are no
     * users to assign a patient to.
     */
    readonly canAssignPatients: boolean;
    /** Asks again, for a failure that may since have cleared. */
    retry: () => void;
};

/** The permissions that reach patient data, as the connector's own policy has it. */
const PATIENT_PERMISSIONS: string[] = [
    PERMISSION.patientView,
    PERMISSION.patientModify,
    PERMISSION.patientImport,
    PERMISSION.patientExport,
    PERMISSION.patientAssign,
];

/**
 * Who the connector says is calling, and what they may do.
 *
 * Asked only where there is a connector to ask. Against the cloud backend the
 * identity comes from the sign-in the application performs itself, and this
 * endpoint does not exist.
 *
 * Who-are-you and what-do-you-hold are different questions, and the connector
 * answers them with different statuses: a caller it cannot identify is refused
 * with 401, and one it identifies who holds nothing with 403. They are kept
 * apart because the remedies have nothing in common — one is the workstation or
 * the directory, the other is an empty group.
 *
 * Asked once per page load and kept: who the caller is does not change within a
 * session, and the connector enforces every request itself, so a stale answer
 * here cannot admit anything the connector would refuse.
 */
export function useConnectorIdentity(): ConnectorIdentity {
    const enabled = AppConfig.isConnectorMode;
    const { data, error, isLoading, isFetching, refetch } = useQuery({
        queryKey: ['api', 'auth', 'whoami'],
        queryFn: async ({ signal }) => {
            const deadline = startDeadline(ANSWER_WITHIN_MS, signal);
            try {
                return await fetchGetWhoAmI(
                    {
                        retryWindowMs: RETRY_WITHIN_MS,
                        responseTimeoutMs: ATTEMPT_RESPONSE_TIMEOUT_MS,
                    },
                    deadline.signal
                );
            } finally {
                deadline.clear();
            }
        },
        retry: false,
        enabled,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
    });

    return useMemo(() => {
        const permissions = data?.identity?.permissions ?? [];
        const permitsAll = data !== undefined && !data.signInEnabled;

        // An answer already in hand settles it, whatever a later ask does. React
        // Query keeps the data and sets the error when a re-check fails, and
        // reading that as a refusal would close the application on somebody who
        // was already working in it — taking whatever they had not saved.
        const failure: unknown = data === undefined ? error : null;
        const status =
            failure instanceof ConnectorNextError ? failure.status : undefined;
        const answeredOtherwise =
            status !== undefined && status !== 401 && status !== 403;

        const can = (permission: string) =>
            permitsAll || permissions.includes(permission);

        return {
            account: data?.identity?.account ?? undefined,
            displayName: data?.identity?.displayName ?? undefined,
            permissions,
            signInEnabled: data?.signInEnabled ?? false,
            isUnidentified: status === 401,
            holdsNothing: status === 403,
            isUnreachable: failure != null && status === undefined,
            failedWithStatus: answeredOtherwise ? status : undefined,
            isLoading: enabled && isLoading,
            // Deliberately not "is a request in flight": a re-check behind an answer we already have
            // would otherwise replace the application with a spinner and take an unsaved contour with it.
            isAsking: enabled && isFetching && data === undefined,
            can,
            reachesPatientData: permissions.some((held) =>
                PATIENT_PERMISSIONS.includes(held)
            ),
            canAssignPatients:
                data?.signInEnabled === true && can(PERMISSION.patientAssign),
            retry: () => void refetch(),
        };
    }, [data, error, isLoading, isFetching, enabled, refetch]);
}
